import { CreditWallet } from './CreditWallet';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import {
  Person,
  Email,
  CalendarToday,
  Payment,
  Cancel,
  CheckCircle,
  AccountCircle,
} from '@mui/icons-material';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getPayPalSubscriptionManagementUrl } from '../utils/paypal';
import axios from 'axios';
import { API_BASE_URL } from '../services/apiConfig';

interface UserAccountProps {
  walletEnabled?: boolean;
  onBack?: () => void;
}

interface UserData {
  id: number;
  email: string;
  username: string;
  is_manager: boolean;
  created_at: string;
}

interface PaymentHistory {
  id: number;
  amount: number;
  currency: string;
  status: string;
  product_name: string;
  description: string;
  created_at: string;
  paypal_payment_id: string;
}

export const UserAccount: React.FC<UserAccountProps> = ({ onBack, walletEnabled = false }) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [payments, setPayments] = useState<PaymentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [error, setError] = useState<string>('');
  
  const { subscription, checkSubscription, cancelSubscription } = useSubscription();

  useEffect(() => {
    fetchUserData();
    fetchPaymentHistory();
  }, []);

  const fetchUserData = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/users/me`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUserData(response.data);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get<PaymentHistory[]>(
        `${API_BASE_URL}/api/payments/history`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPayments(response.data);
    } catch (err) {
      console.error('Error fetching payment history:', err);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    const success = await cancelSubscription();
    
    if (success) {
      await checkSubscription();
      setCancelDialogOpen(false);
      alert('✅ Subscription cancelled. Your access will continue until the expiration date. Please also cancel on PayPal to stop future billing.');
    } else {
      alert('❌ Failed to cancel subscription. Please try again or contact support.');
    }
    
    setCancelling(false);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'trial':
        return 'info';
      case 'active':
        return 'success';
      case 'cancelled':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'FAILED':
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={48} sx={{ color: '#9b59b6' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {onBack && (
        <Button
          onClick={onBack}
          sx={{
            mb: 4,
            color: '#c0c0c0',
            '&:hover': {
              color: '#e0e0e0',
            },
          }}
        >
          ← Back to Home
        </Button>
      )}

      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 700, color: '#c0c0c0' }}
        >
          My Account
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Manage your profile, subscription, and payment information
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {walletEnabled && <CreditWallet />}
      <Grid container spacing={4}>
        {/* User Information */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #404040',
              height: '100%',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AccountCircle sx={{ fontSize: 40, color: '#9b59b6', mr: 2 }} />
                <Typography variant="h5" sx={{ color: '#c0c0c0', fontWeight: 600 }}>
                  Account Information
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Person sx={{ color: '#808080' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#808080' }}>
                      Username
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#e0e0e0', fontWeight: 600 }}>
                      {userData?.username}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Email sx={{ color: '#808080' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#808080' }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#e0e0e0', fontWeight: 600 }}>
                      {userData?.email}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CalendarToday sx={{ color: '#808080' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#808080' }}>
                      Member Since
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#e0e0e0', fontWeight: 600 }}>
                      {userData?.created_at
                        ? new Date(userData.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                {userData?.is_manager && (
                  <Chip
                    label="Manager Account"
                    color="secondary"
                    sx={{ width: 'fit-content', mt: 1 }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Information */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              backgroundColor: '#1a1a1a',
              border: subscription?.is_cancelled ? '1px solid #ff9800' : '1px solid #404040',
              height: '100%',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Payment sx={{ fontSize: 40, color: '#9b59b6', mr: 2 }} />
                  <Typography variant="h5" sx={{ color: '#c0c0c0', fontWeight: 600 }}>
                    Subscription
                  </Typography>
                </Box>
                {subscription?.status && (
                  <Chip
                    label={subscription.status.toUpperCase()}
                    color={getStatusColor(subscription.status) as any}
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>

              {!subscription?.has_active_subscription ? (
                <Alert severity="info">
                  No active subscription. Visit the Products page to subscribe.
                </Alert>
              ) : (
                <>
                  {subscription.is_cancelled && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Your subscription has been cancelled. Access continues until{' '}
                      <strong>
                        {subscription.expires_at
                          ? new Date(subscription.expires_at).toLocaleDateString()
                          : 'expiration'}
                      </strong>
                      .
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#808080' }}>
                        Current Plan
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#e0e0e0', fontWeight: 600 }}>
                        {subscription.plan_name}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: '#808080' }}>
                        Price
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#e0e0e0', fontWeight: 600 }}>
                        ${subscription.price?.toFixed(2)}/{subscription.billing_cycle}
                      </Typography>
                    </Box>

                    {subscription.expires_at && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#808080' }}>
                          {subscription.is_cancelled ? 'Access Until' : 'Next Renewal'}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#e0e0e0', fontWeight: 600 }}>
                          {new Date(subscription.expires_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </Typography>
                      </Box>
                    )}

                    {!subscription.is_cancelled && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setCancelDialogOpen(true)}
                        sx={{
                          mt: 2,
                          borderColor: '#f44336',
                          color: '#f44336',
                          '&:hover': {
                            borderColor: '#d32f2f',
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          },
                        }}
                      >
                        Cancel Subscription
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment History */}
        <Grid item xs={12}>
          <Card
            sx={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #404040',
            }}
          >
            <CardContent>
              <Typography variant="h5" sx={{ color: '#c0c0c0', fontWeight: 600, mb: 3 }}>
                Payment History
              </Typography>

              {payments.length === 0 ? (
                <Alert severity="info">No payment history found.</Alert>
              ) : (
                <TableContainer component={Paper} sx={{ backgroundColor: '#2d2d2d' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#c0c0c0', fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ color: '#c0c0c0', fontWeight: 600 }}>Product</TableCell>
                        <TableCell sx={{ color: '#c0c0c0', fontWeight: 600 }}>Amount</TableCell>
                        <TableCell sx={{ color: '#c0c0c0', fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ color: '#c0c0c0', fontWeight: 600 }}>PayPal ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell sx={{ color: '#e0e0e0' }}>
                            {new Date(payment.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ color: '#e0e0e0' }}>
                            {payment.product_name || payment.description || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: '#e0e0e0' }}>
                            ${payment.amount.toFixed(2)} {payment.currency}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={payment.status}
                              color={getPaymentStatusColor(payment.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#808080', fontSize: '0.75rem' }}>
                            {payment.paypal_payment_id
                              ? `${payment.paypal_payment_id.substring(0, 20)}...`
                              : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cancel Subscription Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            border: '1px solid #404040',
          },
        }}
      >
        <DialogTitle sx={{ color: '#c0c0c0' }}>Cancel Subscription?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#e0e0e0', mb: 2 }}>
            Are you sure you want to cancel your subscription?
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Your access will continue until the expiration date.</strong>
            <br />
            You will retain access to all features until{' '}
            {subscription?.expires_at
              ? new Date(subscription.expires_at).toLocaleDateString()
              : 'your current period ends'}
            .
          </Alert>
          <Alert severity="warning">
            <strong>Important:</strong> You must also cancel your subscription on PayPal to stop
            future automatic billing.
            <br />
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() => window.open(getPayPalSubscriptionManagementUrl(), '_blank')}
            >
              Manage PayPal Subscriptions →
            </Button>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={cancelling}
            sx={{ color: '#a0a0a0' }}
          >
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelSubscription}
            disabled={cancelling}
            variant="contained"
            color="error"
            startIcon={cancelling ? <CircularProgress size={20} /> : <Cancel />}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
